import { Controller } from "@hotwired/stimulus"

// Manages the per-item net + VAT inputs on the payment batch form.
//
// Behavior:
// - Editing "net" auto-scales VAT proportionally, preserving the row's
//   original VAT/net ratio — until the user types into VAT manually, at
//   which point auto-scaling stops for that row (the user wins).
// - Any net+VAT exceeding the invoice's outstanding amount is clamped on
//   blur, and a visible amber warning is shown above the form listing
//   which rows were clamped and why.
// - The server enforces the same clamp on submit, so the JS layer is
//   purely a UX nicety.
export default class extends Controller {
  static targets = ["row", "warningArea"]

  connect() {
    this.rowTargets.forEach((row) => {
      const ratio = parseFloat(row.dataset.vatNetRatio || "0") || 0
      row.dataset._initialRatio = String(ratio)
      row.dataset._vatTouched = "0"
    })
  }

  netChanged(event) {
    const row = event.currentTarget.closest("[data-payment-batch-form-target='row']")
    if (!row) return
    if (row.dataset._vatTouched === "1") {
      this._updateRowTotal(row)
      return
    }

    const netCents = this._parseGrosze(event.currentTarget.value)
    const ratio = parseFloat(row.dataset._initialRatio || "0") || 0
    const vatInput = row.querySelector("[data-role='vat-input']")
    if (vatInput) {
      vatInput.value = this._formatPln(Math.round(netCents * ratio))
    }
    this._updateRowTotal(row)
  }

  vatChanged(event) {
    const row = event.currentTarget.closest("[data-payment-batch-form-target='row']")
    if (!row) return
    // Mark the row as user-edited so further net changes don't overwrite VAT.
    row.dataset._vatTouched = "1"
    this._updateRowTotal(row)
  }

  blurClamp() {
    const warnings = []
    this.rowTargets.forEach((row) => {
      const cap = parseInt(row.dataset.outstandingCents || "0", 10) || 0
      if (cap <= 0) return

      const netInput = row.querySelector("[data-role='net-input']")
      const vatInput = row.querySelector("[data-role='vat-input']")
      if (!netInput || !vatInput) return

      const netCents = this._parseGrosze(netInput.value)
      const vatCents = this._parseGrosze(vatInput.value)
      const total = netCents + vatCents
      if (total <= cap) return

      const invoiceNumber = row.dataset.invoiceNumber || "?"
      const capPln = this._formatPln(cap)
      let newVat = vatCents
      let newNet = netCents
      if (vatCents >= total - cap) {
        newVat = vatCents - (total - cap)
      } else {
        newVat = 0
        newNet = netCents - (total - cap - vatCents)
      }
      vatInput.value = this._formatPln(newVat)
      netInput.value = this._formatPln(newNet)
      warnings.push(
        `Faktura ${invoiceNumber}: kwota przekroczyła pozostałe do zapłaty (${capPln} zł) — wartości zostały dopasowane.`
      )
      this._updateRowTotal(row)
    })

    this._renderWarnings(warnings)
  }

  _renderWarnings(warnings) {
    if (!this.hasWarningAreaTarget) return
    if (warnings.length === 0) {
      this.warningAreaTarget.classList.add("hidden")
      this.warningAreaTarget.innerHTML = ""
      return
    }
    this.warningAreaTarget.classList.remove("hidden")
    this.warningAreaTarget.innerHTML = `
      <div class="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
        <p class="font-medium mb-1">Dopasowano kwoty</p>
        <ul class="ml-5 list-disc space-y-1">
          ${warnings.map((w) => `<li>${this._escape(w)}</li>`).join("")}
        </ul>
      </div>
    `
  }

  _updateRowTotal(row) {
    const netInput = row.querySelector("[data-role='net-input']")
    const vatInput = row.querySelector("[data-role='vat-input']")
    const totalCell = row.querySelector("[data-role='row-total']")
    if (!netInput || !vatInput || !totalCell) return
    const cents = this._parseGrosze(netInput.value) + this._parseGrosze(vatInput.value)
    totalCell.textContent = this._formatPln(cents) + " zł"
  }

  _parseGrosze(value) {
    if (value == null) return 0
    const normalised = String(value).replace(/\s/g, "").replace(",", ".")
    const f = parseFloat(normalised)
    if (Number.isNaN(f)) return 0
    return Math.round(f * 100)
  }

  _formatPln(cents) {
    return (Math.round(cents) / 100).toFixed(2).replace(".", ",")
  }

  _escape(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
